import { useEffect, useRef, useState } from "react";
import { fetchSiteNav } from "../api/siteNavApi";
import SITE_NAV_FALLBACK from "../config/siteNavFallback";

/**
 * Voci header/footer del sito www.stirup.it, per l'header desktop (solo cliente)
 * e il footer dell'app.
 *
 * - Rende SEMPRE in modo sincrono: cache locale se presente, altrimenti fallback
 *   compilato nel bundle. La rete fa solo "upgrade" del dato gia' mostrato.
 * - Non fetcha mai www.stirup.it: legge solo /api/ui/site-nav.
 * - Se il server non e' ancora stato sincronizzato (header vuoto) o e' offline,
 *   si resta su cache / fallback senza errori.
 *
 * Ritorna: { header, footer, source } con source = "network" | "cache" | "fallback".
 */

const STORAGE_KEY = "stir_site_nav_v1";

function isUsable(raw) {
  return (
    !!raw &&
    Array.isArray(raw.header) &&
    raw.header.length > 0 &&
    !!raw.footer &&
    Array.isArray(raw.footer.columns) &&
    raw.footer.columns.length > 0
  );
}

/** Forma sempre sicura: se una sezione manca, si usa quella del fallback. */
function normalize(raw) {
  const header =
    Array.isArray(raw?.header) && raw.header.length > 0
      ? raw.header
      : SITE_NAV_FALLBACK.header;

  const cols = raw?.footer?.columns;
  const footer =
    Array.isArray(cols) && cols.length > 0
      ? {
          columns: cols,
          social: Array.isArray(raw.footer.social) ? raw.footer.social : [],
          // chiave assente (dato salvato prima dei badge) -> badge del fallback;
          // array vuoto esplicito -> nessun badge
          stores: Array.isArray(raw.footer.stores)
            ? raw.footer.stores
            : SITE_NAV_FALLBACK.footer.stores,
          copy: raw.footer.copy || SITE_NAV_FALLBACK.footer.copy,
        }
      : SITE_NAV_FALLBACK.footer;

  return { header, footer };
}

function readCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return isUsable(parsed?.data) ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ data, fetchedAt: Date.now() })
    );
  } catch {
    /* private mode / quota piena: si ignora */
  }
}

function resolveInitial() {
  const cached = readCache();
  if (cached) return { ...normalize(cached), source: "cache" };
  return { ...normalize(null), source: "fallback" };
}

export default function useSiteNav() {
  const [state, setState] = useState(resolveInitial);
  const didFetch = useRef(false);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;

    const ctrl = new AbortController();

    fetchSiteNav({ signal: ctrl.signal })
      .then((json) => {
        if (!isUsable(json)) return; // server non ancora sincronizzato
        writeCache({ header: json.header, footer: json.footer });
        setState({ ...normalize(json), source: "network" });
      })
      .catch(() => {
        /* offline / errore rete: si resta su cache o fallback */
      });

    return () => ctrl.abort();
  }, []);

  return state;
}
